<template>
  <v-img
    @click="$emit('imgClick')"
    :src="imgSrc || src"
    :eager="eager"
    :height="height"
    :width="width"
    :contain="contain"
    v-bind="imageProps"
    @error="(loading = false), (loadError = true)"
    @load="loading = false"
    :aspect-ratio="aspectRatio"
    :max-height="maxHeight"
    :max-width="maxWidth"
    :min-width="minWidth"
    style="flex-grow: 0 0; background-color: #eee"
    :alt="imageAlt"
    :position="imgPosition"
  >
    <slot v-if="loading">
      <v-skeleton-loader type="image" height="100%" width="100%" />
    </slot>

    <slot v-else-if="!loading && loadError" name="loadError">
      <v-sheet
        color="grey lighten-3"
        :height="height"
        :width="width"
        style="position: absolute; top: 0; left: 0; z-index: 0; width: 100%;"
      >
        <v-row no-gutters justify="center" align="center" class="fill-height">
          <v-col cols="auto">
            <v-icon v-if="isProfileImage" large color="grey">
              {{ icons.mdiAccount }}
            </v-icon>

            <img
              v-else
              :src="require('@/assets/trust_logo.svg')"
              :width="placeholderWidth"
              class="pa-1 grey-scale"
            />
          </v-col>
        </v-row>
      </v-sheet>
    </slot>

    <slot></slot>
  </v-img>
</template>

<script>
import { mdiImage, mdiAccount } from '@mdi/js';
import { VUE_APP_FILE_URL } from '@/utilities/constants.js';

export default {
  props: {
    type: {
      required: false,
      type: String
    },

    minWidth: {
      type: String,
      default: () => ''
    },

    fileName: {
      required: false,
      default: () => ''
    },

    profileId: {
      required: false,
      type: String
    },

    projectId: {
      required: false,
      type: String
    },

    itineraryId: {
      type: String,
      required: false
    },

    imageType: {
      required: false,
      type: String,
      default: 'n'
    },

    height: {
      required: false,
      type: String,
      default: '100%'
    },

    width: {
      required: false,
      type: String,
      default: '100%'
    },

    eager: {
      required: false,
      type: Boolean
    },

    imageProps: {
      type: Object,
      required: false,
      default: () => ({})
    },
    contain: {
      type: Boolean,
      default: () => true
    },

    maxHeight: {
      type: String,
      default: () => ''
    },

    maxWidth: {
      type: String,
      default: () => ''
    },

    imgSrc: {
      type: String,
      default: () => ''
    },

    aspectRatio: {
      type: Number,
      required: false,
      default: 1.618
    },

    isProfileImage: {
      type: Boolean,
      required: false,
      default: false
    },

    placeholderWidth: {
      type: String,
      required: false,
      default: '70px'
    },

    imageAlt: {
      type: String,
      default: 'Trust image'
    },

    imgPosition: {
      type: String,
      default: 'center'
    }
  },

  data() {
    return {
      loadError: false,
      loading: true,
      src: '',
      icons: {
        mdiImage,
        mdiAccount
      }
    };
  },

  created() {
    if (!this.imgSrc) {
      this.setUrl();
    }
  },

  methods: {
    validateSrcProps() {
      const srcProps = ['type', 'imageType', 'fileName'];
      const idTypes = ['projectId', 'profileId', 'itineraryId'];

      const missingSrcProps = element => {
        if (!this[element]) return true;
        return false;
      };
      if (srcProps.some(missingSrcProps)) return false;

      const hasId = element => this[element];

      if (!idTypes.some(hasId)) return false;

      return true;
    },

    setUrl() {
      if (this.fileName?.file) {
        this.loadError = true;
        this.loading = false;
        return;
      }
      const isValid = this.validateSrcProps();

      if (!isValid) {
        this.loadError = true;
        this.loading = false;
        this.src = '';
        return;
      }

      let id = '';
      switch (this.type) {
        case 'p':
          id = this.projectId;
          break;
        case 'u':
          id = this.profileId;
          break;
        default:
          break;
        case 'i':
          id = this.itineraryId;
          break;
      }

      this.src = `${VUE_APP_FILE_URL}/${this.type}/${id}/${this.imageType}/${this.fileName}`;
    }
  },

  watch: {
    fileName(newVal, oldVal) {
      if (newVal !== oldVal && !this.imgSrc) {
        this.loading = true;
        this.loadError = false;
        this.setUrl();
      }
    },
    imgSrc(newVal) {
      this.loadError = false;
      this.loading = false;
      this.imgSrc = newVal;
    }
  }
};
</script>

<style scoped>
.v-skeleton-loader >>> .v-skeleton-loader__image {
  height: 100%;
  width: 100%;
}
</style>
